import { ThreeBounce } from "better-react-spinkit";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { Button, Modal, ModalBody, ModalFooter } from "reactstrap";
import CountrySelectService from "../../components/CSC/CountrySelect/CountrySelectService";
import PhoneNumWithValidation from "../../components/PhoneNum/PhoneNumWithValidation";
import OtpInput from "../../pages/Authentication/OtpInput";
import { DialogContext } from "../../store/context/DialogContext";
import { doGET, doPOST, doPUT } from "../../util/HttpUtil";
import { normalizeCountryCode } from "../../util/dialCode";
import GCaptcha from "../Authentication/GCaptcha";
import UserNameField from "./components/userName/UserNameInputField";
import "./PreUboDashboard.scss";

const normalizePhoneData = (phoneData = {}) => ({
  ...phoneData,
  countryCode: normalizeCountryCode(phoneData?.countryCode),
});

const btnStyle = {
  backgroundColor: "#1678AE",
  height: "40px",
  padding: "0 16px",
  fontSize: "16px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFF"
};

const PreUboDashboard = ({ uboData, applicationForm, onSuccess, usernameOnly = false, skipUsername = false }) => {
  const { showError, showMessage } = useContext(DialogContext);
  const [phoneData, setPhoneData] = useState(normalizePhoneData({
    countryCode: uboData?.mobileCountryCode || "",
    phone: uboData?.mobilePhone || "",
    phoneVerifiedViaOTP: uboData?.mobilePhoneVerifiedViaOTP || false
  }));
  const normalizedCountryCode = normalizeCountryCode(phoneData?.countryCode);
  const [username, setUsername] = useState(uboData?.cyclosUserName || "");
  const [isValidUsername, setIsValidUsername] = useState(null);
  const [usernameError, setUsernameError] = useState(null);
  const [loadingUsername, setLoadingUsername] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpId, setOtpId] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);

  const [sysConfingValidateMobileByOTP, setSysConfingValidateMobileByOTP] = useState(true);
  const [otpByPassedCountries, setOtpByPassedCountries] = useState([]);
  const [isCurrentCountryOtpByPassed, setIsCurrentCountryOtpByPassed] = useState(false);
  const usesUnifiedBusinessEndpoints = applicationForm?.formType === "BUSINESS";
  const setIndividualEndpoint = usesUnifiedBusinessEndpoints
    ? "/api/business/set-individual"
    : (usernameOnly ? "/api/business/set-individual" : "/api/business-lite/set-individual");
  const otpVerificationEndpoint = usesUnifiedBusinessEndpoints
    ? "/api/business/individual/otp-verification"
    : "/api/business-lite/individual/otp-verification";

  const shouldPerformOtpVerification = useMemo(() => {
    if (usernameOnly) return false;
    return sysConfingValidateMobileByOTP && !isCurrentCountryOtpByPassed;
  }, [sysConfingValidateMobileByOTP, isCurrentCountryOtpByPassed, usernameOnly]);
  const requiresUsername = !skipUsername;

  const resetCaptchaAndOtp = (refreshCaptcha = false) => {
    setCaptchaError(null);
    setOtp("");
    setOtpId(null);
    setOtpError(null);
    setResendTimer(0);
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      resetCaptchaAndOtp();
    }
  };

  const getSysConfigForMobileOtpVerification = async () => {
    setLoading(true);
    try {
      const response = await doGET("/api/sys-config/validateMobileByOTP/detail");
      if (response?.status === 200) {
        setSysConfingValidateMobileByOTP(response.data?.validateMobileByOTP ?? false);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getOTPByPassedCountries = async () => {
    try {
      const response = await doGET("/api/sys-config/otpBypassCountries/detail");
      if (response?.status === 200) {
        setOtpByPassedCountries(response.data?.otpBypassCountries ?? []);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (usernameOnly) return;
    let isMounted = true;

    const syncOtpBypassState = async () => {
      if (!isMounted) return;

      if (!CountrySelectService.cyclosCountryMaster.length) {
        await CountrySelectService.fetchCyclosCountryMaster();
      }

      if (!normalizedCountryCode) {
        setIsCurrentCountryOtpByPassed(false);
        return;
      }

      const bypassCountrySet = new Set(
        (otpByPassedCountries || []).map((country) => String(country).trim().toUpperCase()).filter(Boolean)
      );
      const countriesForPincode = CountrySelectService.getIsoCodesForDialCode(normalizedCountryCode);
      const isByPassed = countriesForPincode?.some((country) =>
        bypassCountrySet.has(String(country).trim().toUpperCase())
      );
      setIsCurrentCountryOtpByPassed(isByPassed);
    };

    syncOtpBypassState();

    return () => {
      isMounted = false;
    };
  }, [normalizedCountryCode, otpByPassedCountries, usernameOnly]);

  useEffect(() => {
    if (usernameOnly) return;
    getSysConfigForMobileOtpVerification();
    getOTPByPassedCountries();
  }, [usernameOnly]);

  const handlePhoneChange = (newPhoneData) => {
    setPhoneData(normalizePhoneData({ ...newPhoneData, phoneVerifiedViaOTP: false }));
  };

  const handleSendOtp = async () => {
    if (loadingOtp) return;

    if (!recaptchaToken) {
      setCaptchaError("Please complete the CAPTCHA.");
      return;
    }
    setLoadingOtp(true);
    try {
      // const fullPhoneNumber = `${phoneData.countryCode}${phoneData.phone}`;
      const response = await doPOST(`/api/message/send/otp`, {
        // to: fullPhoneNumber,
        cc: normalizedCountryCode,
        no: phoneData.phone,
        ref_id: uboData?.application_id,
        platform: "web",
        individual_id: uboData?._id,
        g_captcha_response: recaptchaToken,
      });

      if (response?.data?._id) {
        setOtpId(response.data._id);
        showMessage("OTP sent successfully!");
        setModalOpen(true);
        setOtpError("");
      } else {
        showError("Failed to send OTP. Please try again.");
        resetCaptchaAndOtp(true);
      }
    } catch (error) {
      showError(error);
      resetCaptchaAndOtp(true);
    } finally {
      setLoadingOtp(false);
    }
  };

  const handleOtpVerification = async () => {
    if (loadingOtp) return;
    if (otp.length !== 4) {
      setOtpError("Please enter a valid 4-digit OTP.");
      return;
    }
    setLoadingOtp(true);
    try {
      const response = await doPUT(otpVerificationEndpoint, {
        ubo_id: uboData?._id,
        form_id: uboData?.application_id,
        otpValue: otp,
        otp_id: otpId,
        phone: phoneData.phone,
        countryCode: normalizedCountryCode,
      });
      if (response.status === 200) {
        showMessage("OTP verified successfully!");
        setPhoneData(normalizePhoneData({ ...phoneData, countryCode: normalizedCountryCode, phoneVerifiedViaOTP: true }));
        setModalOpen(false);
        setOtpError(null);
      } else {
        setOtpError("Invalid OTP. Please try again.");
        resetCaptchaAndOtp(true);
      }
    } catch (error) {
      showError(error);
      resetCaptchaAndOtp(true);
    } finally {
      setLoadingOtp(false);
    }
  };



  const handleNext = async () => {
    if (requiresUsername && !isValidUsername) {
      setUsernameError("Please enter a valid, unique username.");
      return;
    }
    if (!usernameOnly && (shouldPerformOtpVerification ? !phoneData.phoneVerifiedViaOTP : false)) {
      setOtpError("Please verify your phone with OTP.");
      return;
    }

    setLoading(true)
    try {
      if (requiresUsername) {
        await doPUT(setIndividualEndpoint, {
          ubo_id: uboData?._id,
          form_id: uboData?.application_id,
          username: username,
        });
      }
      if (!usernameOnly && !shouldPerformOtpVerification && isValidPhone && (uboData?.mobilePhone !== phoneData.phone || normalizeCountryCode(uboData?.mobileCountryCode) !== normalizedCountryCode)) {
        await doPUT(otpVerificationEndpoint, {
          ubo_id: uboData?._id,
          form_id: uboData?.application_id,
          phone: phoneData.phone,
          countryCode: normalizedCountryCode,
        });
      }
      onSuccess();
    } catch (error) {
      showError("Error updating details. Please try again.");
    } finally {
      setLoading(false)
    }
  };

  const showCaptcha = () => {
    setModalOpen(true);
  };

  return (
    <div className=" d-flex align-items-center justify-content-center">
      <div className="fv_pre_ubo-container mt-3 flex-1">
        <h2 className="fv_pre_ubo-title">Verify Your Details</h2>

        {requiresUsername ? (
          <div className="fv_pre_ubo-input-group">
            <UserNameField
              applicationId={uboData?.application_id}
              individual_id={uboData?._id}
              label="Username"
              required
              className="fv_pre_ubo-username-field"
              value={username}
              onChange={(newUsername) => { setUsername(newUsername); }}
              loading={loadingUsername}
              isValidUsername={isValidUsername}
              setIsValidUsername={setIsValidUsername}
              err={usernameError}
              handleLoading={setLoadingUsername}
            />
          </div>
        ) : null}

        {!usernameOnly && <div className="fv_pre_ubo-input-group-phone">
          <div className="flex-1">

            <PhoneNumWithValidation
              label="Mobile Phone"
              group="Businesses_Lite"
              value={phoneData}
              onChange={handlePhoneChange}
              isDisabled={shouldPerformOtpVerification && phoneData.phoneVerifiedViaOTP && phoneData?.phone}
              notifyParent={(isValid) => setIsValidPhone(isValid)}
              onError={({ errorMsg, hasError }) => setOtpError(hasError ? errorMsg : null)}
              showErr={otpError}
              required
              form_id={uboData?.application_id}
              individual_id={uboData?._id}
              placeholder="Mobile Phone"
            />
            {(shouldPerformOtpVerification && phoneData?.phone && !phoneData?.phoneVerifiedViaOTP && isValidPhone) ?
              <div className="error-message mt-3"> <em className="selectfield__error">{("Please verify mobile phone with OTP")}</em></div> : null}
          </div>

          {shouldPerformOtpVerification ? <div className="ms-2" >
            {phoneData.phoneVerifiedViaOTP ? (
              <FaCheckCircle style={{ marginTop: "50px" }} color="#28a745" title="OTP Verified" className="otp-verified-icon" />
            ) : (
              <Button

                onClick={showCaptcha}
                disabled={!phoneData.phone || !normalizedCountryCode || !isValidPhone || loadingOtp}
                style={{ ...btnStyle, marginTop: "40px" }}
              >
                {loadingOtp ? <ThreeBounce size={10} color="#FFFFFF" /> : "Send OTP"}
              </Button>
            )}
          </div> : null}

        </div>}


        {(!usernameOnly && shouldPerformOtpVerification) ?
          <Modal backdrop="static" isOpen={modalOpen} toggle={toggleModal} centered className="fv_modal-content">
            <ModalBody className="fv_modal-body pt-4">
              {!otpId ? <>
                <p>Solve the CAPTCHA to send OTP:</p>
                <div className="d-flex justify-content-center my-3">
                  <GCaptcha
                    onChange={setRecaptchaToken}
                    onExpired={() => setRecaptchaToken("")}
                  />
                </div>
                {captchaError && <p className="error-text">{captchaError}</p>}
              </> : <>
                <p>Enter the OTP sent to {normalizedCountryCode}-{phoneData.phone}</p>
                <div className="fv_otp-input-container">
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    style={{
                      borderRadius: "14px",
                      border: "1px solid #C6F3FF",
                      height: "50px",
                      width: "50px",
                      textAlign: "center",
                      fontSize: "18px",
                      margin: "0 5px"
                    }}
                  />
                </div>
                {otpError && <div className="fv_error-message">{otpError}</div>}
              </>}

            </ModalBody>
            <ModalFooter>
              {otpId ? <Button
                onClick={handleOtpVerification}
                disabled={otp.length !== 4 || loadingOtp}
                style={btnStyle}
              >
                {loadingOtp ? <ThreeBounce size={10} color="#FFFFFF" /> : "Verify OTP"}
              </Button> : <> <Button
                style={btnStyle}
                onClick={handleSendOtp}
                disabled={loading || resendTimer > 0 || loadingOtp}
              >
                {loading ? <ThreeBounce size={10} color="#FFF" /> : "Send OTP"}
              </Button>
                {resendTimer > 0 && (
                  <p className="timer-text">Resend available in {resendTimer}s</p>
                )}</>}

              <Button
                onClick={toggleModal}
                color="secondary"
                className="fv_btn-secondary"
                style={{
                  backgroundColor: "#f1faff",
                  color: "#007bff",
                  border: "1px solid #c6f3ff",
                  borderRadius: "10px",
                  padding: "8px 16px",
                  fontSize: "16px"
                }}
              >
                Cancel
              </Button>
            </ModalFooter>
          </Modal> : null}

        <Button
          style={btnStyle}
          onClick={handleNext}
          disabled={(requiresUsername && !isValidUsername) || (!usernameOnly && (!isValidPhone || (shouldPerformOtpVerification ? !phoneData.phoneVerifiedViaOTP : false)))}
          className="fv_pre_ubo-button fv_pre_ubo-next-button"
        >
          {loading ? <ThreeBounce size={10} color="#FFFFFF" /> : (requiresUsername ? "Next" : "Continue to KYC")}
        </Button>

      </div>
    </div>

  );
};

export default observer(PreUboDashboard);
